import { config } from './config'
interface AuthResponse {
  AuthenticationResult?: {
    IdToken: string
    AccessToken: string
    RefreshToken: string
    ExpiresIn: number
    TokenType: string
  }
  message?: string
}
class AuthService {
  private token: string | null = null
  private tokenExpiry: number | null = null
  async login(): Promise<string> {
    // Check if we have a valid token in memory
    if (this.token && this.tokenExpiry && Date.now() < this.tokenExpiry) {
      return this.token
    }

    // Check localStorage
    const storedToken = localStorage.getItem('vsm_auth_token')
    const storedExpiry = localStorage.getItem('vsm_token_expiry')
    if (storedToken && storedExpiry && Date.now() < parseInt(storedExpiry)) {
      this.token = storedToken
      this.tokenExpiry = parseInt(storedExpiry)
      return storedToken
    }
    const startTime = performance.now()
    try {
      const response = await fetch(config.cognito.endpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-amz-json-1.1',
          'X-Amz-Target': 'AWSCognitoIdentityProviderService.InitiateAuth',
        },
        body: JSON.stringify({
          AuthFlow: 'USER_PASSWORD_AUTH',
          ClientId: config.cognito.clientId,
          AuthParameters: {
            USERNAME: config.credentials.username,
            PASSWORD: config.credentials.password,
          },
        }),
      })
      const data: AuthResponse = await response.json()
      const durationMs = Math.round(performance.now() - startTime)
      if (data.AuthenticationResult?.IdToken) {
        this.token = data.AuthenticationResult.IdToken
        // Set expiry (usually 1 hour, subtract 5 mins for safety)
        this.tokenExpiry =
          Date.now() + data.AuthenticationResult.ExpiresIn * 1000 - 300000
        localStorage.setItem('vsm_auth_token', this.token)
        localStorage.setItem('vsm_token_expiry', this.tokenExpiry.toString())

        // Log successful auth
        import('../hooks/useDebugLogs')
          .then(({ pushDebugLog }) => {
            pushDebugLog({
              source: 'system',
              tool: 'cognito_login',
              params: {
                username: config.credentials.username,
              },
              rawResponse: {
                expiresIn: data.AuthenticationResult!.ExpiresIn,
                tokenType: data.AuthenticationResult!.TokenType,
              },
              processedData: {
                success: true,
                expiresIn: data.AuthenticationResult!.ExpiresIn,
              },
              success: true,
              durationMs,
            })
          })
          .catch(() => {})
        return this.token
      } else {
        // Log failed auth
        import('../hooks/useDebugLogs')
          .then(({ pushDebugLog }) => {
            pushDebugLog({
              source: 'system',
              tool: 'cognito_login',
              params: {
                username: config.credentials.username,
              },
              rawResponse: data,
              processedData: null,
              success: false,
              error: data.message || 'Authentication failed',
              durationMs,
            })
          })
          .catch(() => {})
        throw new Error(data.message || 'Authentication failed')
      }
    } catch (error) {
      const durationMs = Math.round(performance.now() - startTime)
      // Log error (only if not already logged above)
      if (
        !(error instanceof Error && error.message === 'Authentication failed')
      ) {
        import('../hooks/useDebugLogs')
          .then(({ pushDebugLog }) => {
            pushDebugLog({
              source: 'system',
              tool: 'cognito_login',
              params: {
                username: config.credentials.username,
              },
              rawResponse: null,
              processedData: null,
              success: false,
              error: error instanceof Error ? error.message : 'Login error',
              durationMs,
            })
          })
          .catch(() => {})
      }
      console.error('Login error:', error)
      throw error
    }
  }
  getToken(): string | null {
    return this.token || localStorage.getItem('vsm_auth_token')
  }
  logout() {
    this.token = null
    this.tokenExpiry = null
    localStorage.removeItem('vsm_auth_token')
    localStorage.removeItem('vsm_token_expiry')
  }
}
export const authService = new AuthService()

// ─── React Auth Context ────────────────────────────────────────────────────────
import React, { createContext, useContext, useState, useCallback } from 'react'
import { UserProfile } from './userProfiles'
interface AuthContextType {
  user: UserProfile | null
  isAuthenticated: boolean
  login: (profile: UserProfile) => void
  logout: () => void
}
const AuthContext = createContext<AuthContextType>({
  user: null,
  isAuthenticated: false,
  login: () => {},
  logout: () => {},
})
export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<UserProfile | null>(() => {
    try {
      const saved = localStorage.getItem('vsm_user_profile')
      return saved ? JSON.parse(saved) : null
    } catch {
      return null
    }
  })
  const login = useCallback((profile: UserProfile) => {
    setUser(profile)
    try {
      localStorage.setItem('vsm_user_profile', JSON.stringify(profile))
    } catch {}
  }, [])
  const logout = useCallback(() => {
    setUser(null)
    authService.logout()
    try {
      localStorage.removeItem('vsm_user_profile')
    } catch {}
  }, [])
  return React.createElement(
    AuthContext.Provider,
    {
      value: {
        user,
        isAuthenticated: !!user,
        login,
        logout,
      },
    },
    children,
  )
}
export function useAuth() {
  return useContext(AuthContext)
}
